import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { PropertyTitleFormModal } from "./property-title-form-modal";

interface PropertyTitle {
  id: string;
  securityPropertyId: string;
  titleReference: string;
  state: string;
  status: string | null;
  createdAt: string;
}

export interface PropertyTitlesPanelProps {
  securityId: string;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function PropertyTitlesPanel({ securityId }: PropertyTitlesPanelProps) {
  const { toast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState<PropertyTitle | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    title: PropertyTitle | null;
  }>({ open: false, title: null });

  // Fetch property titles
  const { data: titlesData, isLoading } = useQuery<PropertyTitle[]>({
    queryKey: ["/api/crm/securities", securityId, "property", "titles"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/crm/securities/${securityId}/property/titles`
      );
      return response.json();
    },
    enabled: !!securityId,
  });

  const titles = Array.isArray(titlesData) ? titlesData : [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (titleId: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/crm/securities/${securityId}/property/titles/${titleId}`
      );
      if (!response.ok) {
        throw new Error("Failed to delete title");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/securities", securityId, "property", "titles"],
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some(
            (key) =>
              typeof key === "string" &&
              key.includes(`/api/crm/securities/${securityId}`)
          ),
      });
      toast({
        title: "Success",
        description: "Title deleted successfully",
      });
      setDeleteDialog({ open: false, title: null });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleEditTitle = (title: PropertyTitle) => {
    setEditingTitle(title);
    setModalOpen(true);
  };

  const handleModalClose = (open: boolean) => {
    setModalOpen(open);
    if (!open) setEditingTitle(null);
  };

  const handleAddTitle = () => {
    setEditingTitle(null);
    setModalOpen(true);
  };

  return (
    <>
      <Card
        className="border-0 shadow-none"
        data-testid="panel-property-titles"
      >
        <CardHeader className="px-0 py-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium">
            Property Titles
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={handleAddTitle}
            data-testid="button-add-title"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Title
          </Button>
        </CardHeader>

        <CardContent className="px-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : titles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No title records found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title Reference</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {titles.map((title) => (
                  <TableRow
                    key={title.id}
                    data-testid={`row-title-${title.id}`}
                  >
                    <TableCell className="py-2 px-3 font-medium">
                      {title.titleReference}
                    </TableCell>
                    <TableCell className="py-2 px-3">{title.state}</TableCell>
                    <TableCell className="py-2 px-3">
                      {title.status ?? "requested"}
                    </TableCell>
                    <TableCell className="py-2 px-3">
                      {formatDate(title.createdAt)}
                    </TableCell>
                    <TableCell className="py-2 px-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditTitle(title)}
                        data-testid={`button-edit-title-${title.id}`}
                        aria-label="Edit title"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() =>
                          setDeleteDialog({ open: true, title })
                        }
                        data-testid={`button-delete-title-${title.id}`}
                        aria-label="Delete title"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PropertyTitleFormModal
        open={modalOpen}
        onOpenChange={handleModalClose}
        securityId={securityId}
        existingTitle={editingTitle}
      />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          setDeleteDialog({
            open,
            title: open ? deleteDialog.title : null,
          })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Title</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this title record? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteDialog.title &&
                deleteMutation.mutate(deleteDialog.title.id)
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
